import { type FC, useCallback, useEffect, useState } from "react";

import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import AccountSelectionNoAccounts from "~/features/b2bAccountSelection/components/AccountSelectionNoAccounts";
import Spinner from "~/common/components/Spinner/Spinner";
import { sendData } from "~/domains/user/api/SetAccount";
import { redirectToUrl } from "~/utils/url/urlUtils";
import { TVAccountSelectionPage } from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/TVAccountSelectionPage";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import {
  type AccountInfoData,
  getAccountsData,
} from "~/features/b2bAccountSelection/components/AccountSelectionTabelView/api/GetAccountList";
import { invalidateNavigationCache } from "~/features/navigation/redux/navigationThunks";
import { storageEngine, TYPE } from "~/common/utils/storage";
import { useTranslation } from "react-i18next";
import { redirectToPdp, toBeRedirectedToPdp } from "~/utils/redirectionUtil";
import AccountSelectionView from "~/features/b2bAccountSelection/components/AccountSelectionView/AccountSelectionView";
import { Box, Button, Paper, Stack, Typography, styled } from "~/common/mui";
import { IMG_NO_RESULTS } from "~/utils/url/staticResources";

export interface Props {
  personalUid: string;
  variant: "purchaser" | "salesman";
  /**
   * For mocking and testing
   */
  noAccounts?: boolean;
}

export const setAccount = (accountId: string, salesOrg?: string) =>
  sendData({ accountId, salesOrg })
    .then((response) => response.json())
    .then((body) => {
      window.sessionStorage.setItem("accountChanged", "true");
      // if no body => redirectToUrl(HOMEPAGE);
      invalidateNavigationCache();
      if (toBeRedirectedToPdp()) {
        redirectToPdp();
      } else {
        redirectToUrl(body);
      }
    });

export const AccountSelectionPage: FC<Props> = ({ personalUid, variant, noAccounts = false }) => {
  const { t } = useTranslation();
  // create tile viewe if it has no permison for tabular view
  const [accounts, setAccounts] = useState<AccountInfoData[] | undefined>(undefined);
  const [selectedId, setSelectedId] = useState<string>("");

  useEffect(() => {
    getAccountsData()
      .then(({ rows, selectedAccountId }) => {
        setAccounts(rows);
        setSelectedId(selectedAccountId);
        storageEngine(TYPE.SESSION).setItem("userAccounts", JSON.stringify(rows));
      })
      .catch(() => {
        setAccounts([]);
        storageEngine(TYPE.SESSION).setItem("userAccounts", "[]");
      });
  }, []);

  const setPersonalAccount = useCallback(() => setAccount(personalUid), [personalUid]);

  let content;
  if (!accounts) {
    content = <Spinner />;
  } else if (noAccounts || accounts.length === 0) {
    // It's impossible to end up in this case in variant purchaser.
    // BE should redirect outta this page if purchaser has no accounts to select from
    content = <AccountSelectionNoAccounts onSelectPersonalAccount={setPersonalAccount} />;
  } else {
    content = (
      <>
        <PageTitle title={t(`page.accountSelection.title.${variant}`)} />
        <AccountSelectionView selectedAccountId={selectedId} accounts={accounts} />
      </>
    );
  }
  return content;
};

const StyledPaper = styled(Paper)({
  display: "flex",
  width: "100vw",
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  borderRadius: 0,
  padding: "16px 24px",
  boxSizing: "border-box",
  zIndex: 1,
});

export const AccountSelectionBottomButton = ({ onClick, disabled }: { onClick: () => void; disabled: boolean }) => {
  const { t } = useTranslation();
  return (
    <StyledPaper variant="elevation" elevation={3}>
      <Button disabled={disabled} fullWidth onClick={onClick}>
        {t("accountSelection.buttonText")}
      </Button>
    </StyledPaper>
  );
};

export const NoResultsFound = () => {
  const { t } = useTranslation();
  return (
    <Stack alignItems="center" justifyContent="center" width="100%" sx={{ gap: 3, mt: 3 }}>
      <Typography variant="headlineLarge">{t("accountSelection.noResults.title")}</Typography>
      <img src={IMG_NO_RESULTS} alt={t("accountSelection.noResults.title")} />
      <Typography variant="bodyLarge" textAlign="center">
        {t("accountSelection.noResults.message")}
      </Typography>
    </Stack>
  );
};

export function createAccountSelectionPageComponent(data: Props) {
  const canSeeTabularView = userHasPermission(Permission.SEE_ENDCUSTOMER_TABULAR_VIEW);
  return (
    <div className="scha__page--account-selection">
      {canSeeTabularView ? <TVAccountSelectionPage /> : <AccountSelectionPage {...data} />}
    </div>
  );
}
